import { useMemo, useEffect } from "react";
import { useShallow } from "zustand/react/shallow";
import { useNavigate } from "react-router-dom";

import {
  CommandPaletteAction,
  CommandPaletteActionType,
  CommandPaletteBaseEntityItem,
  CommandPaletteSearchResult,
} from "components/CommandPalette/types";
import { BUCKET_IDS } from "components/CommandPalette/constants";
import { makeSubListItemId } from "components/CommandPalette/utils/ids";
import { selectActiveActionId } from "components/CommandPalette/store/useCommandPaletteStore/selectors";
import Feedback from "ds/components/Feedback";
import { IconComponent } from "types/Icon";

import useCommandPaletteStore from "../../store/useCommandPaletteStore";
import CommandPaletteSubList from "../elements/SubList";
import CommandPaletteActionItem from "../elements/ActionItem";
import CommandPaletteEmptyState from "../elements/EmptyState";

type CommandPaletteEntitySearchProps<T extends CommandPaletteBaseEntityItem> = {
  useSearchHook: (props: { query: string }) => CommandPaletteSearchResult<T>;
  icon: IconComponent;
  pathPrefix: string;
  entityName: string;
};

const CommandPaletteEntitySearch = <T extends CommandPaletteBaseEntityItem>({
  useSearchHook,
  icon,
  pathPrefix,
  entityName,
}: CommandPaletteEntitySearchProps<T>) => {
  const navigate = useNavigate();

  const { subListQuery, breadcrumb, setSubListItems, activeActionId } = useCommandPaletteStore(
    useShallow((state) => ({
      subListQuery: state.subListQuery,
      breadcrumb: state.breadcrumb,
      setSubListItems: state.setSubListItems,
      activeActionId: selectActiveActionId(state),
    }))
  );

  const { items: entities, loading, hasMore, isEmpty } = useSearchHook({ query: subListQuery });

  const items: CommandPaletteAction[] = useMemo(
    () =>
      entities.map((entity) => {
        const path = `${pathPrefix}${entity.id}`;
        return {
          type: CommandPaletteActionType.SUB_LIST_ITEM,
          id: makeSubListItemId(BUCKET_IDS.SEARCH, entity.id),
          title: entity.name,
          icon,
          bucketId: BUCKET_IDS.SEARCH,
          onAction: () => navigate(path),
          metadata: {
            title: entity.spaceDetails.name,
            navigationPath: path,
          },
        };
      }),
    [entities, navigate, pathPrefix, icon]
  );

  useEffect(() => {
    setSubListItems(items, breadcrumb);
  }, [items, breadcrumb, setSubListItems]);

  return (
    <CommandPaletteSubList breadcrumb={breadcrumb} loading={loading}>
      {isEmpty && !loading ? (
        <CommandPaletteEmptyState message={`No ${entityName} found`} />
      ) : (
        items.map((item) => (
          <CommandPaletteActionItem
            key={item.id}
            action={item}
            isActive={item.id === activeActionId}
          />
        ))
      )}

      {hasMore && (
        <Feedback
          type="banner"
          variant="info"
          title={`There are more ${entityName} to show`}
          margin="small 0 0"
        >
          Please make your search term more specific
        </Feedback>
      )}
    </CommandPaletteSubList>
  );
};

export default CommandPaletteEntitySearch;
